/* eslint-disable jsx-a11y/accessible-emoji */
import React from 'react';
import PropTypes from 'prop-types';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { useTheme } from '@nivo/core';

import { emotionMapping, EmotionColors } from '../../utils/emotions';
import './Charts.css';

const emotions = Object.keys(emotionMapping);

const getColorIndex = (val, MAX_VALUE = 1) => {
  const indexer = Math.floor(EmotionColors.length / 2);
  const segmentSize = MAX_VALUE / indexer;

  return Math.floor(val / segmentSize + indexer);
};

const getEmotionColor = val => {
  if (!val) {
    console.log(val);
    return '#ffffff';
  }
  const colorIndex = getColorIndex(val);
  return EmotionColors[
    colorIndex < EmotionColors.length ? colorIndex : EmotionColors.length - 1
  ];
};

const CustomCell = ({
  value,
  data,
  x,
  y,
  width,
  height,
  opacity,
  borderWidth,
  borderColor,
  onHover,
  onLeave,
  onClick
}) => {
  const colorIndex = getColorIndex(value);
  return (
    <g
      transform={`translate(${x}, ${y})`}
      onMouseEnter={onHover}
      onMouseMove={onHover}
      onMouseLeave={onLeave}
      onClick={onClick ? event => onClick(data, event) : undefined}
    >
      <rect
        x={-0.5 * width}
        y={-0.5 * height}
        fill={getEmotionColor(value)}
        width={width}
        height={height}
        fillOpacity={opacity}
        strokeWidth={borderWidth}
        stroke={borderColor}
        style={{ cursor: 'pointer' }}
      />
      <text
        dominantBaseline="central"
        textAnchor="middle"
        style={{
          fontFamily: 'ProximaNovaSb',
          fontSize: '12px',
          opacity,
          fill: `${
            colorIndex <= 2 || colorIndex >= EmotionColors.length - 3
              ? '#ffffff'
              : '#373737'
          }`
        }}
      >
        {value || '-'}
      </text>
    </g>
  );
};

const TopTick = ({
  value: _value,
  x,
  y,
  opacity,
  rotate,
  format,
  lineX,
  lineY,
  onClick,
  textX,
  textY,
  textBaseline,
  textAnchor
}) => {
  const theme = useTheme();

  let value = _value;
  if (format !== undefined) {
    value = format(value);
  }

  let gStyle = { opacity };
  if (onClick) {
    gStyle['cursor'] = 'pointer';
  }

  return (
    <g
      transform={`translate(${x},${y})`}
      {...(onClick ? { onClick: e => onClick(e, value) } : {})}
      style={gStyle}
    >
      <line x1={0} x2={lineX} y1={0} y2={lineY} style={theme.axis.ticks.line} />

      <text
        height={20}
        width={20}
        dominantBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={`translate(${textX},${textY - 20}) rotate(${rotate})`}
        style={{
          height: '20px',
          width: '20px',
          ...theme.axis.ticks.text,
          fontSize: '20px'
        }}
      >
        {emotionMapping[value]}
      </text>
      <text
        dominantBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={`translate(${textX},${textY}) rotate(${rotate})`}
        // (1) merge theme styles with our fontWeight
        style={{
          ...theme.axis.ticks.text,
          fontFamily: 'ProximaNova',
          textTransform: 'uppercase',
          color: '#666666'
        }}
      >
        {value}
      </text>
    </g>
  );
};

const formatSeasonEpisode = value => {
  if (!value) {
    return '';
  }
  const splitValue = value.split('|');
  return splitValue.length > 1 ? `${splitValue[0]} | ${splitValue[1]}` : value;
};

const LeftTick = ({
  value: _value,
  x,
  y,
  opacity,
  rotate,
  format,
  lineX,
  lineY,
  onClick,
  textX,
  textY,
  textBaseline,
  textAnchor
}) => {
  const theme = useTheme();

  let value = _value;
  if (format !== undefined) {
    value = format(value);
  }

  let gStyle = { opacity };
  if (onClick) {
    gStyle['cursor'] = 'pointer';
  }

  const formattedValue = formatSeasonEpisode(value);

  return (
    <g
      transform={`translate(${x},${y})`}
      {...(onClick ? { onClick: e => onClick(e, value) } : {})}
      style={gStyle}
    >
      <line x1={0} x2={lineX} y1={0} y2={lineY} style={theme.axis.ticks.line} />
      <text
        dominantBaseline={textBaseline}
        textAnchor={textAnchor}
        transform={`translate(${textX},${textY}) rotate(${rotate})`}
        style={{
          ...theme.axis.ticks.text,
          fontFamily: 'ProximaNovaSb',
          fontSize: '12px',
          color: '#373737'
        }}
      >
        {formattedValue}
      </text>
    </g>
  );
};

export const EmotionHeatmap = ({ data, loading }) => {
  if (loading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div className="tvt-time-chart__loading-state-wrapper chart-loading-heatmap">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1066"
            height="437"
            viewBox="0 0 1066 437"
          >
            <defs>
              <linearGradient
                id="3hx4u2kyga"
                x1="0%"
                x2="100%"
                y1="50%"
                y2="50%"
              >
                <stop offset="0%" stopColor="#EEEEEE" />
                <stop offset="33%" stopColor="#EEEEEE" />
                <stop offset="50%" stopColor="#f5f5f5" />
                <stop offset="67%" stopColor="#EEEEEE" />
                <stop offset="100%" stopColor="#EEEEEE" />
                <animateTransform
                  attributeName="gradientTransform"
                  type="translate"
                  from="-1 0"
                  to="1 0"
                  begin="0s"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </linearGradient>
            </defs>
            <path
              fill="url(#3hx4u2kyga)"
              fillRule="evenodd"
              d="M1098 495v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zM83 509v14H33v-14h50zm1014-60v42h-78v-42h78zm-902 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-656 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-656 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zM82 463v14H32v-14h50zm523-60v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm902 0v42h-78v-42h78zM82 417v14H32v-14h50zm606-60v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-656 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-656 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-902 0v42h-78v-42h78zM83 371v14H33v-14h50zm440-60v42h-78v-42h78zm82 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm902 0v42h-78v-42h78zM82 325v14H32v-14h50zm277-60v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-164 0v42h-78v-42h78zm328 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm902 0v42h-78v-42h78zm-820 0v42h-78v-42h78zm574 0v42h-78v-42h78zM82 279v14H32v-14h50zm195-60v42h-78v-42h78zm492 0v42h-78v-42h78zm328 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm656 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm410 0v42h-78v-42h78zm-492 0v42h-78v-42h78zm246 0v42h-78v-42h78zm-492 0v42h-78v-42h78zM82 233v14H32v-14h50zm687-60v42h-78v-42h78zm-574 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-410 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-738 0v42h-78v-42h78zm82 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zM82 187v14H32v-14h50zm359-60v42h-78v-42h78zm492 0v42h-78v-42h78zm-656 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-574 0v42h-78v-42h78zm820 0v42h-78v-42h78zm-656 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-246 0v42h-78v-42h78zm492 0v42h-78v-42h78zm-574 0v42h-78v-42h78zm164 0v42h-78v-42h78zM82 141v14H32v-14h50zm441-60v42h-78V81h78zm82 0v42h-78V81h78zm246 0v42h-78V81h78zm164 0v42h-78V81h78zm-820 0v42h-78V81h78zm82 0v42h-78V81h78zm820 0v42h-78V81h78zm-410 0v42h-78V81h78zm-328 0v42h-78V81h78zm410 0v42h-78V81h78zm164 0v42h-78V81h78zm-492 0v42h-78V81h78zM82 95v14H32V95h50z"
              transform="translate(-32 -81)"
            />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveHeatMap
      data={data}
      height={50 * data.length + 100}
      keys={emotions}
      indexBy="episodeString"
      cellShape={CustomCell}
      cellOpacity={1}
      hoverTarget="rowColumn"
      cellBorderColor="#fff"
      defs={[
        {
          id: 'lines',
          type: 'patternLines',
          background: 'inherit',
          color: 'rgba(0, 0, 0, 0.1)',
          rotation: -45,
          lineWidth: 4,
          spacing: 7
        }
      ]}
      cellBorderWidth={4}
      margin={{ top: 80, right: 60, bottom: 60, left: 100 }}
      axisTop={{
        orient: 'top',
        tickSize: 0,
        tickPadding: 20,
        tickRotation: 0,
        legend: '',
        legendOffset: 36,
        renderTick: TopTick
      }}
      axisLeft={{
        orient: 'left',
        tickSize: 0,
        tickPadding: 35,
        tickRotation: 0,
        legendPosition: 'middle',
        legendOffset: -40,
        renderTick: LeftTick
      }}
      tooltip={d => {
        const { value, xKey, yKey } = d;

        return (
          <div
            style={{
              color: '#ffffff',
              width: '108px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              fontFamily: 'ProximaNovaSb',
              padding: '10px 0'
            }}
          >
            <div style={{ fontSize: '20px' }}>{emotionMapping[xKey]}</div>
            <div
              style={{
                textTransform: 'uppercase',
                fontSize: '10px',
                height: '14px',
                lineHeight: '14px',
                letterSpacing: '0.5px'
              }}
            >
              {xKey}
            </div>
            <div
              style={{
                fontFamily: 'ProximaNovaB',
                fontSize: '12px',
                marginBottom: '8px',
                height: '12px',
                lineHeight: 1,
                letterSpacing: '0.5px'
              }}
            >
              {formatSeasonEpisode(yKey)}
            </div>
            <div style={{ fontSize: '14px', fontFamily: 'ProximaNovaSb' }}>
              <span style={{ color: '#c0c0c0', fontFamily: 'ProximaNova' }}>
                Index:
              </span>
              <span> {value}</span>
            </div>
          </div>
        );
      }}
      theme={{
        tooltip: {
          container: {
            background: '#333'
          }
        }
      }}
    />
  );
};

EmotionHeatmap.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  loading: PropTypes.bool
};

EmotionHeatmap.defaultProps = {
  data: [],
  loading: true
};

export default EmotionHeatmap;
